import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import { tr } from 'date-fns/locale';
import { Gauge, RefreshCw } from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { ProviderSlug, UsageRow } from '@runik/shared';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Spinner } from '../ui/spinner';
import { EmptyState } from '../data/EmptyState';
import { toast } from '../ui/sonner';
import { ApiError } from '../../lib/auth/client';
import { listUsage, refreshUsage, usageKey } from '../../lib/queries/usage';

interface Props {
  customerId?: string;
  providerSlug?: ProviderSlug;
  from?: string;
  to?: string;
}

export function UsageView({ customerId, providerSlug, from, to }: Props) {
  const qc = useQueryClient();
  const filters = { customerId, providerSlug, from, to };
  const usage = useQuery({
    queryKey: usageKey(filters),
    queryFn: () => listUsage(filters),
  });

  const [lastRefreshAt, setLastRefreshAt] = useState<Date | null>(null);

  const refresh = useMutation({
    mutationFn: () => refreshUsage(filters),
    onSuccess: (resp) => {
      setLastRefreshAt(new Date());
      if (resp.status === 'no_credentials') {
        toast.info('Aktif kimlik bilgisi bulunamadı');
      } else {
        toast.success(`${resp.queued} kimlik bilgisi için snapshot kuyruğa alındı`);
      }
      setTimeout(() => qc.invalidateQueries({ queryKey: ['usage'] }), 3000);
    },
    onError: (err) => {
      if (err instanceof ApiError && err.problem.status === 429) {
        toast.error('Çok hızlı tetiklediniz — dakikada 1 istek sınırı.');
      } else {
        toast.error(err instanceof ApiError ? err.problem.detail ?? err.problem.title : 'Yenileme başarısız');
      }
    },
  });

  const chartData = useMemo(() => {
    const buckets = new Map<number, { ts: number; cost: number }>();
    for (const r of usage.data ?? []) {
      // Snapshot'lar UTC bazlı saklanıyor; UTC midnight'a hizalayıp aynı UTC günündeki
      // farklı credential'ları topla. timezone shift'i bucket key'ini bozmasın.
      const d = new Date(r.periodStart);
      const ts = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
      const cost = Number(r.costUsd);
      const existing = buckets.get(ts);
      if (existing) existing.cost += cost;
      else buckets.set(ts, { ts, cost });
    }
    return Array.from(buckets.values())
      .sort((a, b) => a.ts - b.ts)
      .map((b) => ({
        day: new Date(b.ts).toLocaleDateString('tr-TR', {
          month: 'short',
          day: 'numeric',
          timeZone: 'UTC',
        }),
        cost: b.cost,
      }));
  }, [usage.data]);

  const rangeTotal = useMemo(
    () => (usage.data ?? []).reduce((sum, r) => sum + Number(r.costUsd), 0),
    [usage.data],
  );

  const chartTitle = useMemo(() => {
    if (chartData.length === 0) return 'Kullanım Grafiği';
    if (chartData.length === 1) return `${chartData[0]!.day} kullanımı`;
    return `${chartData[0]!.day} → ${chartData[chartData.length - 1]!.day}`;
  }, [chartData]);

  return (
    <div className="p-6 space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm text-slate-500">Aralık Toplamı</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold">${rangeTotal.toFixed(2)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-sm text-slate-500">Snapshot Sayısı</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold">{usage.data?.length ?? 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-sm text-slate-500">Yenileme</CardTitle>
          </CardHeader>
          <CardContent className="flex items-center justify-between gap-2">
            <span className="text-xs text-slate-500">
              {lastRefreshAt
                ? formatDistanceToNow(lastRefreshAt, { addSuffix: true, locale: tr })
                : 'Henüz yok'}
            </span>
            <Button
              size="sm"
              variant="outline"
              onClick={() => refresh.mutate()}
              disabled={refresh.isPending}
            >
              {refresh.isPending ? <Spinner /> : <RefreshCw className="h-4 w-4" />}
              Yenile
            </Button>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{chartTitle}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-72">
            {usage.isLoading ? (
              <div className="flex h-full items-center justify-center text-slate-400">
                <Spinner className="h-6 w-6" />
              </div>
            ) : chartData.length === 0 ? (
              <EmptyState icon={Gauge} title="Henüz veri yok" description="İlk snapshot'tan sonra burada grafik görünecek." />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="day" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="cost" fill="#4f46e5" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
